/**
 * useAssetAssessment — fetch a single asset's assessment (八要素质量 + 称重 X/Y +
 * industryRank) for the Detail page's Details card.
 *
 * Independent of useAssetDetail: the backend `asset_assessment` table is 1:1
 * with `assets` but may be absent for assets that have not been scored yet — a
 * missing record surfaces as `assessment=null` (fields render as 占位符 in the
 * header), NOT as an error. The two flows (八要素打分 / 称重) also fill their
 * columns independently, so a "partial" row is possible: one or both of
 * `eightElementQuality` / `scoreX+scoreY` may be missing while the row exists
 * — the header decides per-cell whether it has data to render.
 *
 * Errors are swallowed to `null` on purpose: the Details card is one section of
 * a larger page; a broken assessment op must NOT block the rest of the header
 * from rendering.
 */
import { useEffect, useState } from 'react';
import {
  getIvtClient,
  GetAssetAssessmentCommand,
  type AssetAssessmentDetail,
} from '../../../client/IvtHubClient';

export interface AssetAssessmentData {
  assessment: AssetAssessmentDetail | null;
  loading: boolean;
  reload: () => void;
}

export function useAssetAssessment(assetId: string | null): AssetAssessmentData {
  const [assessment, setAssessment] = useState<AssetAssessmentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!assetId) {
      setAssessment(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Clear stale data from the previously-selected asset. Without this, when
    // the user switches between two VALID assetIds (e.g. clicking Asset B in
    // the nav tree while viewing Asset A), the header would briefly render
    // Asset A's scores / quadrant / report link ON Asset B's page until the
    // new fetch resolves. That's a data-identity mismatch — the worst kind
    // of UI bug because users can't tell the value is stale.
    setAssessment(null);
    (async () => {
      try {
        const out = await getIvtClient().send(new GetAssetAssessmentCommand({ assetId }));
        if (cancelled) return;
        setAssessment(out.assessment ?? null);
      } catch {
        // Assessment is optional for the page — never block rendering on a
        // fetch error; the header falls back to "尚未打分" 占位符.
        if (cancelled) return;
        setAssessment(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId, reloadKey]);

  return { assessment, loading, reload: () => setReloadKey((k) => k + 1) };
}
